import JSZip from 'jszip';

export type TSeshatPoint = [number, number];
export interface ISeshatGeometry {
  id: string;
  kind: string;
  points: TSeshatPoint[];
  ellipse?: {
    major_radius: number;
    minor_radius: number;
    angle_degrees: number;
    basis: { x_axis: TSeshatPoint; y_axis: TSeshatPoint };
  } | null;
}
export interface ISeshatDamage {
  id: string;
  display_number: string;
  page_id: string;
  classifications: {
    code: string;
    properties: {
      key: string;
      unit: string;
      automatic?: { value: number } | null;
      manual?: { value: number } | null;
    }[];
  }[];
  geometries: ISeshatGeometry[];
  notes: string[];
}
export interface ISeshatDocument {
  format: string;
  schema_version: number;
  title: string;
  assets: { id: string; name: string }[];
  sources: { id: string; asset_id: string; kind: string }[];
  pages: {
    id: string;
    source_id: string;
    index: number;
    width_pt: number;
    height_pt: number;
    coordinate_space: string;
  }[];
  drawing_layouts: { page_id: string; frame: { corners: TSeshatPoint[] } }[];
  damages: ISeshatDamage[];
}
export interface IDxfEntity {
  kind: string;
  values: [number, string][];
}
export interface ISeshatImport {
  document: ISeshatDocument;
  drawings: Record<string, IDxfEntity[]>;
}

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
const point = (p: unknown): p is TSeshatPoint => Array.isArray(p) && p.length === 2 && p.every(finite);

// Only model-space ENTITIES are drawn. Blocks, hatches and CAD font layout need a full CAD renderer.
const parseDxf = (text: string): IDxfEntity[] => {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/);
  const entities: IDxfEntity[] = [];
  let inEntities = false;
  let entity: IDxfEntity | undefined;
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = Number(lines[i].trim());
    const value = lines[i + 1].trim();
    if (code === 2 && value === 'ENTITIES') {
      inEntities = true;
      continue;
    }
    if (!inEntities) continue;
    if (code === 0) {
      if (value === 'ENDSEC') break;
      entity = { kind: value, values: [] };
      entities.push(entity);
    } else entity?.values.push([code, value]);
  }
  return entities.filter((e) => !e.values.some(([code, value]) => code === 67 && value === '1'));
};

export const readSeshat = async (file: File): Promise<ISeshatImport> => {
  if (file.size > 40 * 1024 * 1024) throw new Error('40MB 이하의 파일을 선택해주세요.');
  const zip = await JSZip.loadAsync(file);
  const entry = zip.file('document.json');
  if (!entry) throw new Error('document.json이 없는 파일이에요.');
  const document: ISeshatDocument = JSON.parse(await entry.async('string'));
  if (
    document?.format !== 'seshat-work' ||
    document.schema_version !== 4 ||
    !Array.isArray(document.pages) ||
    !document.pages.length ||
    !Array.isArray(document.damages) ||
    !Array.isArray(document.drawing_layouts) ||
    !Array.isArray(document.sources) ||
    !Array.isArray(document.assets)
  ) {
    throw new Error('seshat-work 버전 4 파일만 지원해요.');
  }
  if (
    typeof document.title !== 'string' ||
    !document.pages.every((page) => page && typeof page.id === 'string' && typeof page.source_id === 'string') ||
    !document.sources.every(
      (source) => source && typeof source.id === 'string' && typeof source.asset_id === 'string',
    ) ||
    !document.drawing_layouts.every((layout) => layout && typeof layout.page_id === 'string')
  ) {
    throw new Error('도면 연결 정보가 올바르지 않아요.');
  }
  if (new Set(document.pages.map((page) => page.id)).size !== document.pages.length)
    throw new Error('도면 페이지 ID가 중복돼요.');
  for (const page of document.pages) {
    if (
      !finite(page.width_pt) ||
      !finite(page.height_pt) ||
      page.width_pt <= 0 ||
      page.height_pt <= 0 ||
      page.coordinate_space !== 'normalized_page'
    )
      throw new Error('지원하지 않는 페이지 좌표계예요.');
    const corners = document.drawing_layouts.find((layout) => layout.page_id === page.id)?.frame?.corners;
    if (!corners || corners.length !== 4 || !corners.every(point))
      throw new Error('페이지의 CAD 영역 정보가 올바르지 않아요.');
    const [a, b, , c] = corners;
    if (Math.abs((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])) < 1e-9)
      throw new Error('CAD 영역의 크기가 올바르지 않아요.');
  }
  for (const damage of document.damages) {
    if (
      !damage ||
      typeof damage.id !== 'string' ||
      !document.pages.some((page) => page.id === damage.page_id) ||
      !Array.isArray(damage.geometries) ||
      !damage.geometries.every(
        (g) =>
          g &&
          typeof g.id === 'string' &&
          typeof g.kind === 'string' &&
          Array.isArray(g.points) &&
          g.points.every(point),
      ) ||
      !Array.isArray(damage.classifications) ||
      !damage.classifications.every(
        (c) =>
          c &&
          typeof c.code === 'string' &&
          Array.isArray(c.properties) &&
          c.properties.every(
            (property) =>
              property &&
              typeof property.key === 'string' &&
              typeof property.unit === 'string' &&
              (property.manual == null || finite(property.manual.value)) &&
              (property.automatic == null || finite(property.automatic.value)),
          ),
      ) ||
      !Array.isArray(damage.notes) ||
      !damage.notes.every((note) => typeof note === 'string')
    )
      throw new Error('손상 정보가 올바르지 않아요.');
  }
  const drawings: Record<string, IDxfEntity[]> = {};
  for (const source of document.sources) {
    if (source.kind !== 'dxf') continue;
    const asset = zip.file(`assets/${source.asset_id}`);
    if (asset) {
      const bytes = await asset.async('uint8array');
      const header = new TextDecoder().decode(bytes.slice(0, 10000));
      const encoding = /ANSI_949|ANSI_1361/.test(header) ? 'euc-kr' : 'utf-8';
      drawings[source.id] = parseDxf(new TextDecoder(encoding).decode(bytes));
    }
  }
  if (document.pages.some((p) => !drawings[p.source_id])) throw new Error('페이지에 연결된 DXF 원본이 없어요.');
  return { document, drawings };
};

export const geometryPoints = (geometry: ISeshatGeometry): TSeshatPoint[] => {
  const ellipse = geometry.ellipse;
  if (geometry.kind !== 'ellipse' || !ellipse || !geometry.points[0]) return geometry.points;
  if (
    !finite(ellipse.major_radius) ||
    !finite(ellipse.minor_radius) ||
    !finite(ellipse.angle_degrees) ||
    !point(ellipse.basis?.x_axis) ||
    !point(ellipse.basis?.y_axis)
  )
    return [];
  const center = geometry.points[0];
  const angle = (ellipse.angle_degrees * Math.PI) / 180;
  return Array.from({ length: 65 }, (_, index): TSeshatPoint => {
    const t = (index / 64) * Math.PI * 2;
    const x =
      ellipse.major_radius * Math.cos(t) * Math.cos(angle) - ellipse.minor_radius * Math.sin(t) * Math.sin(angle);
    const y =
      ellipse.major_radius * Math.cos(t) * Math.sin(angle) + ellipse.minor_radius * Math.sin(t) * Math.cos(angle);
    return [
      center[0] + x * ellipse.basis.x_axis[0] + y * ellipse.basis.y_axis[0],
      center[1] + x * ellipse.basis.x_axis[1] + y * ellipse.basis.y_axis[1],
    ];
  });
};

export const drawSeshatBackground = (canvas: HTMLCanvasElement, data: ISeshatImport, pageId: string) => {
  const page = data.document.pages.find((p) => p.id === pageId);
  const frame = data.document.drawing_layouts.find((layout) => layout.page_id === pageId)?.frame;
  if (!page || !frame) return;
  const scale = Math.min(2400 / page.width_pt, 4000 / page.height_pt);
  const width = Math.max(1, Math.round(page.width_pt * scale));
  const height = Math.max(1, Math.round(page.height_pt * scale));
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = '#88929c';
  ctx.fillStyle = '#697580';
  ctx.lineWidth = 0.7;
  const [a, b, , c] = frame.corners;
  const ux = b[0] - a[0],
    uy = b[1] - a[1],
    vx = c[0] - a[0],
    vy = c[1] - a[1];
  const determinant = ux * vy - uy * vx;
  const transform = (x: number, y: number): TSeshatPoint => [
    (((x - a[0]) * vy - (y - a[1]) * vx) / determinant) * width,
    ((ux * (y - a[1]) - uy * (x - a[0])) / determinant) * height,
  ];
  for (const entity of data.drawings[page.source_id] ?? []) {
    const get = (code: number, fallback = 0) => Number(entity.values.find(([key]) => key === code)?.[1] ?? fallback);
    const points: TSeshatPoint[] = [];
    if (entity.kind === 'LINE') points.push([get(10), get(20)], [get(11), get(21)]);
    if (entity.kind === 'LWPOLYLINE' || entity.kind === 'LEADER') {
      let x: number | undefined;
      for (const [code, value] of entity.values) {
        if (code === 10) x = Number(value);
        if (code === 20 && x !== undefined) {
          points.push([x, Number(value)]);
          x = undefined;
        }
      }
      if (entity.kind === 'LWPOLYLINE' && get(70) & 1 && points[0]) points.push(points[0]);
    }
    if (entity.kind === 'CIRCLE' || entity.kind === 'ARC' || entity.kind === 'ELLIPSE') {
      const ellipse = entity.kind === 'ELLIPSE';
      const start = ellipse ? get(41) : entity.kind === 'ARC' ? (get(50) * Math.PI) / 180 : 0;
      let end = ellipse ? get(42, Math.PI * 2) : entity.kind === 'ARC' ? (get(51) * Math.PI) / 180 : Math.PI * 2;
      if (end <= start) end += Math.PI * 2;
      for (let i = 0; i <= 64; i++) {
        const t = start + ((end - start) * i) / 64;
        const x = ellipse ? get(11) * Math.cos(t) - get(21) * get(40) * Math.sin(t) : get(40) * Math.cos(t);
        const y = ellipse ? get(21) * Math.cos(t) + get(11) * get(40) * Math.sin(t) : get(40) * Math.sin(t);
        points.push([get(10) + x, get(20) + y]);
      }
    }
    if (points.length) {
      const converted = points.map(([x, y]) => transform(x, y));
      ctx.beginPath();
      converted.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
      ctx.stroke();
    }
    if (entity.kind === 'TEXT' || entity.kind === 'MTEXT') {
      const [x, y] = transform(get(10), get(20));
      if (x < 0 || y < 0 || x > width || y > height) continue;
      const text = entity.values
        .filter(([code]) => code === 1 || code === 3)
        .map(([, value]) => value)
        .join('')
        .replace(/\\P/g, ' ')
        .replace(/\\U\+([0-9a-f]{4})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate((-get(50) * Math.PI) / 180);
      ctx.font = `${Math.max(4, (get(40) / Math.hypot(ux, uy)) * width)}px sans-serif`;
      ctx.fillText(text, 0, 0);
      ctx.restore();
    }
  }
};
